import React from 'react'
import { X, Clock, User, Calendar } from 'lucide-react'
import { format } from 'date-fns'

const ViewShiftModal = ({ isOpen, onClose, shift }) => {
  if (!isOpen || !shift) return null

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = 'N/A') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A'
    try {
      const [hours, minutes] = timeString.split(':')
      const date = new Date()
      date.setHours(parseInt(hours), parseInt(minutes))
      return format(date, 'h:mm a')
    } catch {
      return timeString
    }
  }

  // Calculate total hours
  const calculateTotalHours = () => {
    const workingHours = safeGet(shift, 'workingHours', 0)
    const breakHours = safeGet(shift, 'breakHours', 0)
    return workingHours + breakHours
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900">Shift Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Shift Name
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {safeGet(shift, 'shiftName')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Shift Code
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {safeGet(shift, 'shiftCode')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Status
                </label>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  safeGet(shift, 'status') === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {safeGet(shift, 'status', 'Unknown').charAt(0).toUpperCase() + 
                   safeGet(shift, 'status', 'Unknown').slice(1)}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Working Hours
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {safeGet(shift, 'workingHours', 0)} Hours
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Break Hours
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {safeGet(shift, 'breakHours', 0)} Hour{safeGet(shift, 'breakHours', 0) !== 1 ? 's' : ''}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Total Hours
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {calculateTotalHours()} Hours
                </p>
              </div>
            </div>
          </div>

          {/* Time Information */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">Time Schedule</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Start Time
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatTime(safeGet(shift, 'startTime'))}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  End Time
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatTime(safeGet(shift, 'endTime'))}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {safeGet(shift, 'notes') && safeGet(shift, 'notes') !== 'N/A' && (
            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-500 mb-2">
                Notes
              </label>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">
                  {safeGet(shift, 'notes')}
                </p>
              </div>
            </div>
          )}

          {/* Creation Information */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-200">Creation Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-gray-400" />
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Created By
                  </label>
                  <p className="text-sm text-gray-900">
                    {safeGet(shift, 'createdBy.username')} ({safeGet(shift, 'createdBy.role')})
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Created At
                  </label>
                  <p className="text-sm text-gray-900">
                    {safeGet(shift, 'createdAt') !== 'N/A' 
                      ? format(new Date(shift.createdAt), 'PPP p')
                      : 'N/A'
                    }
                  </p>
                </div>
              </div>
            </div>

            {safeGet(shift, 'updatedAt') !== safeGet(shift, 'createdAt') && (
              <div className="mt-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Last Updated
                    </label>
                    <p className="text-sm text-gray-900">
                      {safeGet(shift, 'updatedAt') !== 'N/A' 
                        ? format(new Date(shift.updatedAt), 'PPP p')
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewShiftModal
